import React, { useEffect, useState } from 'react';
import { CheckCircle, Warning, X } from 'phosphor-react';
import { UploadFileInputs } from './UploadFileInput';
import { firebaseLargeFileUploadHelper } from '../../../../utils/helpers';
import { BASE_API_URL } from '../../../../utils/constants';
import { COLORS } from '../../../../utils/colors';
import { ExtractedTradeReference } from '@dill/dill-shared';

export interface TradeReferenceUploadValue {
  name: string;
  url: string;
  fileType: string;
  extractedReferenceCount?: number;
  extractedReferences?: ExtractedTradeReference[];
}

interface Props {
  value: TradeReferenceUploadValue | null;
  onChange: (value: TradeReferenceUploadValue | null) => void;
  onAcceptExtractedReferences: (references: ExtractedTradeReference[]) => void;
  errorText?: string;
  isRequired?: boolean;
  supplierId: string;
  maxReferenceSlots: number;
  autoAddsExtraSlots?: boolean;
}

type Status = 'idle' | 'uploading' | 'extracting' | 'done' | 'error';

const AppTradeReferenceUpload: React.FC<Props> = ({
  value,
  onChange,
  onAcceptExtractedReferences,
  errorText,
  isRequired,
  supplierId,
  maxReferenceSlots,
  autoAddsExtraSlots,
}) => {
  const [status, setStatus] = useState<Status>(value ? 'done' : 'idle');
  const [warning, setWarning] = useState<string | null>(null);
  const [extractedReferences, setExtractedReferences] = useState<ExtractedTradeReference[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (value && status !== 'uploading' && status !== 'extracting') {
      setStatus('done');
    } else if (!value && status === 'done') {
      setStatus('idle');
      setWarning(null);
      setErrorMessage(null);
      setExtractedReferences([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const referencesToShow =
    extractedReferences.length > 0 ? extractedReferences : value?.extractedReferences ?? [];
  const referenceCount = referencesToShow.length || value?.extractedReferenceCount || 0;

  const handleFileSelected = async ({ file }: { name: string; file: File }) => {
    setStatus('uploading');
    setWarning(null);
    setErrorMessage(null);

    try {
      const uploadUrl = await firebaseLargeFileUploadHelper({
        blobUrl: URL.createObjectURL(file),
        fileNameWithExtension: file.name,
        storagePath: `creditApplicationsFiles/${supplierId}/${Date.now()}`,
      });
      if (!uploadUrl) throw new Error('Upload failed. Please try again.');

      setStatus('extracting');

      const response = await fetch(`${BASE_API_URL}/creditApplications/extractTradeReferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileUrl: uploadUrl, mimeType: file.type, supplierId }),
      });
      const json = await response.json();

      if (!response.ok || json.errors) {
        throw new Error(json?.errors?.[0]?.msg || 'Extraction failed. Please try again.');
      }

      const references: ExtractedTradeReference[] = json.data?.references || [];

      onAcceptExtractedReferences(references);

      if (references.length === 0) {
        setErrorMessage(
          'No trade references were found in this file. Please upload a different file, or fill in the details manually below.'
        );
        setStatus('error');
        return;
      }

      onChange({
        url: uploadUrl,
        name: file.name,
        fileType: file.name.split('.').pop() || '',
        extractedReferenceCount: references.length,
        extractedReferences: references,
      });
      setExtractedReferences(references);

      const overflowWarning =
        references.length > maxReferenceSlots && !autoAddsExtraSlots
          ? `This file has ${references.length} references, but only ${maxReferenceSlots} could be filled in below — add another trade reference and enter the rest manually.`
          : null;
      setWarning(overflowWarning || json.data?.warning || null);
      setStatus('done');
    } catch (error: any) {
      console.error('Trade reference extraction error:', error);
      setErrorMessage(error.message || 'Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  const handleRemoveFile = () => {
    onChange(null);
    setStatus('idle');
    setWarning(null);
    setErrorMessage(null);
    setExtractedReferences([]);
  };

  return (
    <div className="flex flex-col gap-3 w-full">
      {status !== 'done' && (
        <UploadFileInputs
          label="Trade reference sheet"
          labelNode={
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold GREY_800-CLR">
                Upload a trade-reference sheet
              </span>
              {isRequired && <span className="text-xs font-medium ERROR_500-CLR">Required</span>}
            </span>
          }
          isRequired={isRequired}
          errorText={errorText}
          selectedFileName={value?.name}
          onSelectFile={handleFileSelected}
          onRemoveFile={value ? handleRemoveFile : undefined}
          acceptedFileText="PDF or image files · one file"
          invalidFileMessage="Please upload a PDF or image file."
          onDropProps={{
            accept: {
              'application/pdf': ['.pdf'],
              'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.tif', '.tiff'],
            },
            maxFiles: 1,
          }}
          size="large"
        />
      )}

      {(status === 'uploading' || status === 'extracting') && (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center gap-2 rounded-lg border GREY_200-BORDER GREY_50-BG px-3 py-2 text-sm GREY_600-CLR">
          <span className="h-3.5 w-3.5 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
          <span>
            {status === 'uploading' ? 'Uploading your sheet…' : 'Reading trade references…'}
          </span>
        </div>
      )}

      {status === 'done' && (
        <div
          role="status"
          aria-live="polite"
          className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm ${
            referenceCount > 0
              ? 'SUCCESS_100-BG SUCCESS_200-BORDER SUCCESS_700-CLR'
              : 'PRIMARY_50-BG PRIMARY_200-BORDER PRIMARY_700-CLR'
          }`}>
          <CheckCircle
            size={17}
            weight="fill"
            color={COLORS.SUCCESS_600}
            className="flex-shrink-0"
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold">{value?.name || 'Trade reference sheet'}</p>
            <p className="mt-0.5">
              {referenceCount > 0
                ? `Found ${referenceCount} trade reference${
                    referenceCount > 1 ? 's' : ''
                  }. Review the details below before continuing.`
                : 'No trade references were found. Enter the details manually below.'}
            </p>
          </div>
          <div className="flex flex-shrink-0 items-center gap-1">
            <button
              type="button"
              aria-label="Remove trade reference sheet"
              onClick={handleRemoveFile}
              className="min-h-11 min-w-11 inline-flex items-center justify-center rounded-md ERROR_500-CLR transition-colors hover:ERROR_100-BG focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-1">
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {status === 'done' && referenceCount > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
          {referencesToShow.map((ref, refIdx) => {
            const fullAddress = [
              ref.address,
              ref.city,
              [ref.state, ref.zip].filter(Boolean).join(' '),
            ]
              .filter(Boolean)
              .join(', ');
            return (
              <div
                key={refIdx}
                className="flex flex-col gap-0.5 rounded-lg border GREY_200-BORDER GREY_50-BG px-2 py-1.5 text-xs">
                <span className="font-semibold GREY_800-CLR truncate">
                  {ref.companyName || 'Unnamed company'}
                </span>
                {ref.contactName && (
                  <span className="GREY_600-CLR truncate">{ref.contactName}</span>
                )}
                {ref.email && <span className="GREY_600-CLR truncate">{ref.email}</span>}
                {ref.phone && <span className="GREY_600-CLR truncate">{ref.phone}</span>}
                {ref.accountNumber && (
                  <span className="GREY_600-CLR truncate">Acct# {ref.accountNumber}</span>
                )}
                {fullAddress && <span className="GREY_600-CLR truncate">{fullAddress}</span>}
              </div>
            );
          })}
        </div>
      )}

      {warning && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-lg border WARNING_200-BORDER WARNING_100-BG px-3 py-2.5 text-sm WARNING_700-CLR">
          <Warning
            size={17}
            weight="fill"
            color={COLORS.WARNING_600}
            className="flex-shrink-0 mt-0.5"
            aria-hidden="true"
          />
          <span>{warning}</span>
        </div>
      )}

      {status === 'error' && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border ERROR_200-BORDER ERROR_100-BG px-3 py-2.5 text-sm ERROR_600-CLR">
          <X
            size={17}
            weight="bold"
            color={COLORS.ERROR_500}
            className="flex-shrink-0 mt-0.5"
            aria-hidden="true"
          />
          <span>{errorMessage} You can still fill in the fields below manually.</span>
        </div>
      )}
    </div>
  );
};

export default AppTradeReferenceUpload;
